import { Component, EventEmitter, forwardRef, HostListener, Input, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-qep-dropdown',
  templateUrl: './qep-dropdown.component.html',
  styleUrls: ['./qep-dropdown.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => QepDropdownComponent),
      multi: true
    }
  ]
})
export class QepDropdownComponent implements ControlValueAccessor {

  @Input() options: any[] = [];
  @Input() placeholder = "Select";
  @Input() optionLabel = 'label';
  @Input() optionValue = 'value';
  @Input() disabled: boolean;
  @Input() type: 'solid' | 'outlined' = 'solid';
  @Input() appendTo = 'body';
  @Input() multi = false;
  @Output() detectChange = new EventEmitter<any>();
  @Input() maxSelectedLabels : number = 2;
  @Input() selectedPrefix: string =  '';
  @Input() selectedItemsLabel: string = '';
  @Input() display: 'comma' | 'chip' = 'comma';

  @Input() optionDisableHandler: (selected: any, option: any) => boolean;
  @Input() borderColor: string ;
  @Input() filterable = false;
  @ViewChild('multiSelect') multiSelect: any;
  @Input() showToggleAll: boolean = false;
  @Input() selectAllLabel: string = 'Select All';
  @Input() searchInput = false;

  searchText = '';
  value: any = '';

  readonly NO_RESULTS_SENTINEL = '__qep_no_results__';

  get filteredOptions(): any[] {
    if (!this.searchText) return this.options;
    const lower = this.searchText.toLowerCase();
    return this.options.filter(opt =>
      String(opt[this.optionLabel] ?? opt).toLowerCase().includes(lower)
    );
  }

  get displayOptions(): any[] {
    const filtered = this.filteredOptions;
    if (this.searchText && filtered.length === 0) {
      return [{ [this.optionLabel]: '', [this.optionValue]: this.NO_RESULTS_SENTINEL }];
    }
    return filtered;
  }

  onPanelHide(): void {
    this.searchText = '';
  }

  private propagateChange = (_val: any) => {};

  @HostListener('window:scroll', ['$event'])
  onScroll() {
    if (this.multiSelect?.overlayVisible) {
      this.multiSelect.hide();
    }
  }

  writeValue(value: any): void {
    if (this.multi) {
      this.value = Array.isArray(value) ? [...value] : [];
    } else {
      this.value = value;
    }
  }

  onDropdownChange(value: any) {
    if (this.multi) {
      this.value = Array.isArray(value) ? [...value] : [];
    } else {
      this.value = value;
    }
    this.propagateChange(this.value);
    this.detectChange.emit(this.value);
  }


  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(): void {
    // Write function
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  getOptionLabel(option: any): string {
    if (!option) return '';
    if (typeof option === 'object') return option[this.optionLabel] ?? '';
    const found = this.options?.find(o => o[this.optionValue] === option);
    return found ? found[this.optionLabel] : String(option);
  }

  customSelectedLabel(selectedValues: any[], options: any[], optionLabel: string): string {
    if (!selectedValues || selectedValues.length === 0) return '';
    const selectedLabels = selectedValues.map(val => {
      const found = options.find(opt => opt.value === val);
      return found ? found[optionLabel] : val;
    });

    const joined = selectedLabels.join(', ');
    const maxLength = 30;
    return joined.length > maxLength ? joined.substring(0, maxLength) + '...' : joined;
  }

  disableWrapper = (option: any): boolean => {
    if (option[this.optionValue] === this.NO_RESULTS_SENTINEL) return true;
    return this.optionDisableHandler
      ? this.optionDisableHandler(this.value, option)
      : false;
  };
}